import type { PageDimensionsMm } from './geometry';
import type { CompositionLogo, CompositionLogoMimeType, NormalizedRect } from './model';

export const COMPOSITION_LOGO_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg';
export const COMPOSITION_LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const COMPOSITION_LOGO_MAX_DIMENSION_PX = 8192;
export const COMPOSITION_LOGO_MIN_NORMALIZED_SIZE = 0.001;
export const COMPOSITION_LOGO_MAX_ASPECT_RATIO = 100;
export const COMPOSITION_LOGO_FILENAME_MAX_LENGTH = 255;

/** The authored frame may resize on one axis; the image always retains its intrinsic ratio. */
export function containedLogoBounds(bounds: NormalizedRect, aspectRatio: number): NormalizedRect {
  const width = Math.min(bounds.width, bounds.height * aspectRatio);
  const height = width / aspectRatio;
  return {
    x: bounds.x + (bounds.width - width) / 2,
    y: bounds.y + (bounds.height - height) / 2,
    width,
    height,
  };
}

const DATA_URL_PREFIX: Readonly<Record<CompositionLogoMimeType, string>> = {
  'image/png': 'data:image/png;base64,',
  'image/jpeg': 'data:image/jpeg;base64,',
};

export class CompositionLogoImportError extends Error {}

export function isUsableCompositionLogoFilename(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= COMPOSITION_LOGO_FILENAME_MAX_LENGTH &&
    value.trim() === value &&
    [...value].every((character) => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127;
    })
  );
}

export function compositionLogoDisplayName(logo: CompositionLogo): string {
  return isUsableCompositionLogoFilename(logo.filename) ? logo.filename : 'Logo';
}

function detectedMimeType(bytes: Uint8Array): CompositionLogoMimeType | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  )
    return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'image/jpeg';
  return null;
}

function encodeBase64(bytes: Uint8Array): string {
  const chunks: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 0x8000)));
  }
  return btoa(chunks.join(''));
}

export function decodeCompositionLogoData(logo: CompositionLogo): Uint8Array {
  const prefix = DATA_URL_PREFIX[logo.mimeType];
  if (!logo.dataUrl.startsWith(prefix)) throw new Error('Logo data URL type does not match.');
  const payload = logo.dataUrl.slice(prefix.length);
  const binary = atob(payload);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytes.byteLength === 0 || bytes.byteLength > COMPOSITION_LOGO_MAX_BYTES)
    throw new Error('Logo data is outside the supported size range.');
  if (detectedMimeType(bytes) !== logo.mimeType) throw new Error('Logo data is not a valid image.');
  return bytes;
}

export function isCompositionLogoDataValid(logo: CompositionLogo): boolean {
  try {
    decodeCompositionLogoData(logo);
    return true;
  } catch {
    return false;
  }
}

export function defaultCompositionLogoBounds(
  imageWidth: number,
  imageHeight: number,
  page: PageDimensionsMm,
): NormalizedRect {
  const maximumWidthMm = Math.min(40, page.width * 0.25);
  const maximumHeightMm = Math.min(30, page.height * 0.25);
  const scale = Math.min(maximumWidthMm / imageWidth, maximumHeightMm / imageHeight);
  const widthMm = imageWidth * scale;
  const heightMm = imageHeight * scale;
  const marginMm = 5;
  return {
    x: Math.max(0, 1 - (marginMm + widthMm) / page.width),
    y: Math.min(0.95, marginMm / page.height),
    width: widthMm / page.width,
    height: heightMm / page.height,
  };
}

export function compositionLogoBoundsForPage(
  bounds: NormalizedRect,
  aspectRatio: number,
  page: PageDimensionsMm,
): NormalizedRect {
  let width = Math.min(bounds.width, 1 - bounds.x);
  let height = (width * page.width) / (aspectRatio * page.height);
  if (height > 1 - bounds.y) {
    height = 1 - bounds.y;
    width = (height * aspectRatio * page.height) / page.width;
  }
  return { ...bounds, width, height };
}

export function compositionLogoWidthRangeMm(
  bounds: NormalizedRect,
  aspectRatio: number,
  page: PageDimensionsMm,
): { readonly minimum: number; readonly maximum: number } {
  return {
    minimum: Math.max(
      COMPOSITION_LOGO_MIN_NORMALIZED_SIZE * page.width,
      COMPOSITION_LOGO_MIN_NORMALIZED_SIZE * page.height * aspectRatio,
    ),
    maximum: Math.min((1 - bounds.x) * page.width, (1 - bounds.y) * page.height * aspectRatio),
  };
}

export function compositionLogoBoundsForWidthMm(
  bounds: NormalizedRect,
  aspectRatio: number,
  page: PageDimensionsMm,
  requestedWidthMm: number,
): NormalizedRect | null {
  if (!Number.isFinite(requestedWidthMm) || requestedWidthMm <= 0) return null;
  const range = compositionLogoWidthRangeMm(bounds, aspectRatio, page);
  const widthMm = Math.min(range.maximum, Math.max(range.minimum, requestedWidthMm));
  return {
    ...bounds,
    width: widthMm / page.width,
    height: widthMm / aspectRatio / page.height,
  };
}

export type CompositionLogoDimensionDecoder = (
  dataUrl: string,
) => Promise<{ readonly width: number; readonly height: number }>;

export const decodeCompositionLogoDimensions: CompositionLogoDimensionDecoder = async (dataUrl) => {
  const image = new Image();
  image.src = dataUrl;
  try {
    await image.decode();
  } catch (cause) {
    throw new CompositionLogoImportError('The selected image could not be decoded.', { cause });
  }
  if (!image.naturalWidth || !image.naturalHeight)
    throw new CompositionLogoImportError('The selected image has invalid dimensions.');
  return { width: image.naturalWidth, height: image.naturalHeight };
};

export async function importCompositionLogoFile(
  file: File,
  page: PageDimensionsMm,
  decodeDimensions: CompositionLogoDimensionDecoder = decodeCompositionLogoDimensions,
): Promise<CompositionLogo> {
  if (file.size > COMPOSITION_LOGO_MAX_BYTES) {
    throw new CompositionLogoImportError('Choose a PNG or JPEG no larger than 5 MiB.');
  }
  if (file.size === 0) throw new CompositionLogoImportError('The selected image is empty.');
  if (file.type && !['image/png', 'image/jpeg', 'image/jpg'].includes(file.type.toLowerCase())) {
    throw new CompositionLogoImportError('Choose a PNG or JPEG image.');
  }

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (cause) {
    throw new CompositionLogoImportError('The selected image could not be read.', { cause });
  }
  const mimeType = detectedMimeType(bytes);
  if (!mimeType) throw new CompositionLogoImportError('Choose a valid PNG or JPEG image.');
  const dataUrl = `${DATA_URL_PREFIX[mimeType]}${encodeBase64(bytes)}`;
  const dimensions = await decodeDimensions(dataUrl);
  if (
    !Number.isFinite(dimensions.width) ||
    !Number.isFinite(dimensions.height) ||
    dimensions.width <= 0 ||
    dimensions.height <= 0
  ) {
    throw new CompositionLogoImportError('The selected image has invalid dimensions.');
  }
  if (
    dimensions.width > COMPOSITION_LOGO_MAX_DIMENSION_PX ||
    dimensions.height > COMPOSITION_LOGO_MAX_DIMENSION_PX
  ) {
    throw new CompositionLogoImportError(
      'Choose an image no larger than 8,192 pixels in either dimension.',
    );
  }
  const aspectRatio = dimensions.width / dimensions.height;
  if (
    aspectRatio > COMPOSITION_LOGO_MAX_ASPECT_RATIO ||
    aspectRatio < 1 / COMPOSITION_LOGO_MAX_ASPECT_RATIO
  ) {
    throw new CompositionLogoImportError('Choose an image with a less extreme aspect ratio.');
  }
  return {
    ...(isUsableCompositionLogoFilename(file.name) ? { filename: file.name } : {}),
    mimeType,
    dataUrl,
    aspectRatio,
    bounds: defaultCompositionLogoBounds(dimensions.width, dimensions.height, page),
  };
}
